<template>
  <div class="revenue-chart-wrapper">
    <div ref="chartDom" class="chart-container"></div>

    <!-- 全屏对话框 -->
    <v-dialog v-model="isFullscreen" :width="dialogWidth" :height="dialogHeight" persistent class="fullscreen-dialog">
      <v-card class="fullscreen-card">
        <!-- 对话框标题栏 -->
        <div class="fullscreen-header">
          <div class="fullscreen-title">收益数据</div>
          <v-icon icon="mdi-close" class="close-icon" @click="closeFullscreen"></v-icon>
        </div>

        <!-- 放大的内容区域 -->
        <div class="fullscreen-body">
          <RevenueChartInner :data="data" class="fullscreen-chart-container" />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick, computed, defineComponent, h } from 'vue'
import { graphic } from 'echarts'
import { initChart } from '@/utils'

const props = defineProps({
  data: {
    type: Object,
    default: () => ({ categories: [], data: [] })
  }
})

const chartDom = ref(null)
let chartObj = null
let resizeObserver = null

// 全屏状态
const isFullscreen = ref(false)

// 窗口尺寸
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1920)
const windowHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 1080)

const updateWindowSize = () => {
  if (typeof window !== 'undefined') {
    windowWidth.value = window.innerWidth
    windowHeight.value = window.innerHeight
  }
}

// 计算对话框宽度
const dialogWidth = computed(() => {
  const maxWidth = Math.min(windowWidth.value * 0.9, 1400)
  return Math.floor(maxWidth)
})

// 计算对话框高度
const dialogHeight = computed(() => {
  const maxHeight = Math.min(windowHeight.value * 0.9, 900)
  return Math.floor(maxHeight)
})

// 格式化数字，保留2位小数
const formatNumber = (num) => {
  return Number(num || 0).toFixed(2)
}

const chartOption = computed(() => {
  const { categories, data } = props.data

  return {
    grid: {
      top: 20,
      bottom: 40,
      left: 60,
      right: 20
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: {
        fontSize: 12,
        fontFamily: 'HarmonyOS Sans SC',
        fontWeight: 300,
        color: 'rgba(46, 46, 46, 0.70)'
      },
      axisLine: {
        show: false
      }
    },
    yAxis: {
      type: 'value',
      name: '收益（元）',
      nameTextStyle: {
        fontSize: 12,
        fontFamily: 'HarmonyOS Sans SC',
        fontWeight: 300,
        color: 'rgba(46, 46, 46, 0.70)'
      },
      axisLabel: {
        fontSize: 12,
        fontFamily: 'HarmonyOS Sans SC',
        fontWeight: 300,
        color: 'rgba(46, 46, 46, 0.70)'
      },
      splitLine: {
        lineStyle: {
          color: 'rgba(106, 114, 124, 0.1)',
          type: 'dashed'
        }
      }
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0.90)',
      borderColor: 'transparent',
      borderRadius: 16,
      extraCssText: 'backdrop-filter: blur(4px); box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);',
      textStyle: {
        color: '#2E2E2E',
        fontFamily: "HarmonyOS Sans SC",
        fontSize: 14
      },
      axisPointer: {
        type: 'line'
      },
      formatter: (params) => {
        if (params && params.length > 0) {
          const value = params[0].value
          const dotColor = '#21B6FF'
          return `<div style="color: rgba(46, 46, 46, 0.70); font-family: 'HarmonyOS Sans SC'; font-size: 12px; margin-bottom: 4px;">${params[0].name}</div>
                     <div style="display: flex; align-items: center; gap: 4px; color: #2E2E2E; font-family: 'HarmonyOS Sans SC'; font-size: 14px;">
                      <span style="display:inline-block;margin-right:4px;border-radius:10px;width:6px;height:6px;background-color:${dotColor};"></span>
                      收益: ${formatNumber(value)} 元</div>`
        }
        return ''
      }
    },
    series: [{
      data: data.map((value) => {
        // 确保 value 是数字类型
        const numValue = Number(value) || 0
        // 判断是否为0值
        const isZero = numValue === 0

        let borderRadius
        if (isZero) {
          borderRadius = [0, 0, 0, 0]
        } else if (numValue > 0) {
          // 正数：顶部圆角
          borderRadius = [10, 10, 0, 0]
        } else {
          // 负数：底部圆角
          borderRadius = [0, 0, 10, 10]
        }

        // 所有数据都使用蓝色渐变（底部浅色，顶部深色）
        let color
        if (isZero) {
          // 0值：灰色
          color = 'rgba(106, 114, 124, 0.3)'
        } else {
          // 所有非零值：蓝色渐变
          color = graphic.LinearGradient(0, 1, 0, 0, [{
            offset: 0,
            color: '#A1E0FF'
          },
          {
            offset: 1,
            color: '#21B6FF'
          }
          ])
        }

        return {
          value: numValue,
          itemStyle: {
            borderRadius: borderRadius,
            color: color
          }
        }
      }),
      type: 'bar',
      barWidth: 14,
      colorBy: 'data',
      emphasis: {
        itemStyle: {
          color: graphic.LinearGradient(0, 1, 0, 0, [{
            offset: 0,
            color: '#A1E0FF'
          },
          {
            offset: 1,
            color: '#21B6FF'
          }
          ])
        }
      }
    }]
  }
})

const updateChart = () => {
  if (!chartObj) return
  chartObj.setOption(chartOption.value)
}

const initChartInstance = () => {
  if (!chartDom.value) return
  chartObj = initChart(chartDom.value)
  updateChart()

  // 监听容器大小变化
  resizeObserver = new ResizeObserver(() => {
    chartObj?.resize()
  })
  if (chartDom.value) {
    resizeObserver.observe(chartDom.value)
  }
}

// 内部图表组件
const RevenueChartInner = defineComponent({
  props: {
    data: {
      type: Object,
      default: () => ({ categories: [], data: [] })
    }
  },
  setup(props) {
    const chartDom = ref(null)
    let chartObj = null
    let resizeObserver = null

    const formatNumber = (num) => {
      return Number(num || 0).toFixed(2)
    }

    const chartOption = computed(() => {
      const { categories, data } = props.data

      return {
        grid: {
          top: 20,
          bottom: 40,
          left: 60,
          right: 20
        },
        xAxis: {
          type: 'category',
          data: categories,
          axisLabel: {
            fontSize: 12,
            fontFamily: 'HarmonyOS Sans SC',
            fontWeight: 300,
            color: 'rgba(46, 46, 46, 0.70)'
          },
          axisLine: {
            show: false
          }
        },
        yAxis: {
          type: 'value',
          name: '收益（元）',
          nameTextStyle: {
            fontSize: 12,
            fontFamily: 'HarmonyOS Sans SC',
            fontWeight: 300,
            color: 'rgba(46, 46, 46, 0.70)'
          },
          axisLabel: {
            fontSize: 12,
            fontFamily: 'HarmonyOS Sans SC',
            fontWeight: 300,
            color: 'rgba(46, 46, 46, 0.70)'
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(106, 114, 124, 0.1)',
              type: 'dashed'
            }
          }
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255, 255, 255, 0.90)',
          borderColor: 'transparent',
          borderRadius: 16,
          extraCssText: 'backdrop-filter: blur(4px); box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);',
          textStyle: {
            color: '#2E2E2E',
            fontFamily: "HarmonyOS Sans SC",
            fontSize: 14
          },
          axisPointer: {
            type: 'line'
          },
          formatter: (params) => {
            if (params && params.length > 0) {
              const value = params[0].value
              const dotColor = '#21B6FF'
              return `<div style="color: rgba(46, 46, 46, 0.70); font-family: 'HarmonyOS Sans SC'; font-size: 12px; margin-bottom: 4px;">${params[0].name}</div>
                         <div style="display: flex; align-items: center; gap: 4px; color: #2E2E2E; font-family: 'HarmonyOS Sans SC'; font-size: 14px;">
                          <span style="display:inline-block;margin-right:4px;border-radius:10px;width:6px;height:6px;background-color:${dotColor};"></span>
                          收益: ${formatNumber(value)} 元</div>`
            }
            return ''
          }
        },
        series: [{
          data: data.map((value) => {
            const numValue = Number(value) || 0
            const isZero = numValue === 0

            let borderRadius
            if (isZero) {
              borderRadius = [0, 0, 0, 0]
            } else if (numValue > 0) {
              borderRadius = [10, 10, 0, 0]
            } else {
              borderRadius = [0, 0, 10, 10]
            }

            let color
            if (isZero) {
              color = 'rgba(106, 114, 124, 0.3)'
            } else {
              color = graphic.LinearGradient(0, 1, 0, 0, [{
                offset: 0,
                color: '#A1E0FF'
              },
              {
                offset: 1,
                color: '#21B6FF'
              }
              ])
            }

            return {
              value: numValue,
              itemStyle: {
                borderRadius: borderRadius,
                color: color
              }
            }
          }),
          type: 'bar',
          barWidth: 14
        }]
      }
    })

    const initChartInstance = () => {
      if (!chartDom.value) return
      chartObj = initChart(chartDom.value)
      chartObj.setOption(chartOption.value)

      resizeObserver = new ResizeObserver(() => {
        chartObj?.resize()
      })
      if (chartDom.value) {
        resizeObserver.observe(chartDom.value)
      }
    }

    watch(() => props.data, () => {
      if (chartObj) {
        chartObj.setOption(chartOption.value)
      }
    }, { deep: true })

    watch(() => chartOption.value, () => {
      if (chartObj) {
        chartObj.setOption(chartOption.value)
      }
    })

    onMounted(() => {
      nextTick(() => {
        initChartInstance()
      })
    })

    onUnmounted(() => {
      if (resizeObserver) {
        resizeObserver.disconnect()
        resizeObserver = null
      }
      if (chartObj) {
        chartObj.dispose()
        chartObj = null
      }
    })

    return () => h('div', { ref: chartDom, class: 'chart-container' })
  }
})

// 打开全屏（暴露给父组件调用）
const openFullscreen = () => {
  isFullscreen.value = true
}

// 暴露方法给父组件
defineExpose({
  openFullscreen
})

// 关闭全屏
const closeFullscreen = () => {
  isFullscreen.value = false
}

// 监听数据变化，更新普通图表
watch(() => props.data, () => {
  updateChart()
}, { deep: true })

onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', updateWindowSize)
    updateWindowSize()
  }
  nextTick(() => {
    initChartInstance()
  })
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateWindowSize)
  }
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  if (chartObj) {
    chartObj.dispose()
    chartObj = null
  }
})
</script>

<style scoped lang="scss">
.revenue-chart-wrapper {
  width: 100%;
  height: 100%;
  max-height: 400px;
}

.chart-container {
  width: 100%;
  height: 100%;
}

// 全屏对话框样式
.fullscreen-dialog {
  :deep(.v-overlay__content) {
    max-width: none !important;
    max-height: none !important;
  }
}

.fullscreen-card {
  border-radius: 30px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: rgba(255, 255, 255, 1);
}

.fullscreen-header {
  position: relative;
  height: 60px;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  flex-shrink: 0;
  z-index: 10;
}

.fullscreen-title {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  font-size: 20px;
  color: rgba(46, 46, 46, 1);
  font-weight: 500;
  line-height: normal;
}

.close-icon {
  cursor: pointer;
  transition: transform 0.2s ease;
  color: #000;

  &:hover {
    transform: scale(1.1);
  }
}

.fullscreen-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: transparent;
  padding: 20px;
}

.fullscreen-chart-container {
  width: 100%;
  height: 100%;
  min-height: 400px;
}
</style>
